// Mobile-specific data service that extends SharedDataService
// Uses AsyncStorage for React Native

import AsyncStorage from '@react-native-async-storage/async-storage';
import SharedDataService, { SharedAppointment, SharedUser, SharedBusinessData } from './sharedDataService';

class MobileDataService extends SharedDataService {
  private static instance: MobileDataService;

  public static getInstance(): MobileDataService {
    if (!MobileDataService.instance) {
      MobileDataService.instance = new MobileDataService();
    }
    return MobileDataService.instance;
  }

  protected async saveToStorage() {
    try {
      await AsyncStorage.setItem('sharedData', JSON.stringify(this.data));
    } catch (error) {
      console.error('Error saving shared data to AsyncStorage:', error);
    }
  }

  protected async loadFromStorage() {
    try {
      const stored = await AsyncStorage.getItem('sharedData');
      if (stored) {
        this.data = JSON.parse(stored);
      }
    } catch (error) {
      console.error('Error loading shared data from AsyncStorage:', error);
    }
  }

  // Override methods to make them async for AsyncStorage
  public async createAppointment(appointment: Omit<SharedAppointment, 'id' | 'createdAt' | 'updatedAt'>): Promise<SharedAppointment> {
    const newAppointment: SharedAppointment = {
      ...appointment,
      id: `apt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    this.data.appointments.push(newAppointment);
    await this.saveToStorage();
    return newAppointment;
  }

  public async updateAppointment(id: string, updates: Partial<SharedAppointment>): Promise<SharedAppointment | null> {
    const index = this.data.appointments.findIndex(apt => apt.id === id);
    if (index !== -1) {
      this.data.appointments[index] = {
        ...this.data.appointments[index],
        ...updates,
        updatedAt: new Date().toISOString()
      };
      await this.saveToStorage();
      return this.data.appointments[index];
    }
    return null;
  }

  public async deleteAppointment(id: string): Promise<boolean> {
    const index = this.data.appointments.findIndex(apt => apt.id === id);
    if (index !== -1) {
      this.data.appointments.splice(index, 1);
      await this.saveToStorage();
      return true;
    }
    return false;
  }

  public async createUser(user: Omit<SharedUser, 'id' | 'createdAt'>): Promise<SharedUser> {
    const newUser: SharedUser = {
      ...user,
      id: `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      createdAt: new Date().toISOString()
    };
    
    this.data.users.push(newUser);
    await this.saveToStorage();
    return newUser;
  }

  public async syncData() {
    await this.loadFromStorage();
  }

  // Initialize data on app start
  public async initialize() {
    await this.loadFromStorage();
    if (this.data.appointments.length === 0 && this.data.users.length === 0) {
      // Initialize with default data if no data exists
      this.initializeDefaultData();
      await this.saveToStorage();
    }
  }
}

export default MobileDataService;




